import { useQuery } from '@tanstack/react-query'
import { Award, BarChart3, Beaker, BookOpen, ClipboardCheck, GraduationCap, Lightbulb, Rocket, Users } from 'lucide-react'
import { Link, useParams } from 'react-router-dom'
import { PageHero, SectionTitle } from '../components/Common'
import { api } from '../services/api'

const iconMap = {
  award: Award,
  book: BookOpen,
  graduation: GraduationCap,
  lightbulb: Lightbulb,
  rocket: Rocket,
  users: Users,
}

const mediaSrc = (url?: string, fallback = '/assets/images/training-main.jpg') => url || fallback

const programTypeLabels: Record<string, string> = {
  curriculum: 'Chương trình chính khóa',
  subject: 'Theo môn học',
  extracurricular: 'Hoạt động ngoài giờ',
  gifted_support: 'Bồi dưỡng - phụ đạo',
}

export function TrainingPage(){
  const { data: programs = [], isLoading, isError } = useQuery({
    queryKey: ['programs'],
    queryFn: api.getPrograms,
  })
  const { data: page } = useQuery({
    queryKey: ['training-page'],
    queryFn: api.getTrainingPage,
  })

  const directionValues = page?.directionValues?.length ? page.directionValues : [
    { icon: 'book', title: 'Tri thức vững vàng', description: 'Trang bị kiến thức cơ bản và nâng cao.' },
    { icon: 'users', title: 'Kỹ năng toàn diện', description: 'Rèn luyện kỹ năng sống, kỹ năng mềm.' },
    { icon: 'award', title: 'Nhân cách tốt đẹp', description: 'Giáo dục đạo đức, lối sống, trách nhiệm.' },
    { icon: 'rocket', title: 'Sáng tạo - Hội nhập', description: 'Phát huy năng lực sáng tạo, hội nhập.' },
  ]
  const achievementBullets = page?.achievementBullets?.length ? page.achievementBullets : [
    { text: 'Nhiều năm liền đạt danh hiệu Tập thể Lao động tiên tiến.' },
    { text: 'Tỷ lệ học sinh tốt nghiệp THCS đạt 100%.' },
    { text: 'Học sinh giỏi cấp thành phố, cấp tỉnh đạt nhiều giải cao.' },
    { text: 'Đội ngũ giáo viên đạt danh hiệu giáo viên dạy giỏi các cấp.' },
  ]
  const achievementStats = page?.achievementStats?.length ? page.achievementStats : [
    { icon: 'graduation', value: '1.200+', label: 'Học sinh toàn trường' },
    { icon: 'users', value: '40+', label: 'Giáo viên và nhân viên' },
    { icon: 'award', value: '150+', label: 'Giải học sinh giỏi' },
    { icon: 'lightbulb', value: '25+', label: 'Năm đạt danh hiệu tiên tiến' },
  ]
  const galleryImages = page?.galleryImages?.length
    ? page.galleryImages
    : ['training-main.jpg','training-program.jpg','training-subjects.jpg','training-stem.jpg','activity-football.jpg','opening-detail.jpg'].map((image) => ({ url: `/assets/images/${image}`, alt: 'Hoạt động dạy học' }))

  return (
    <>
      <PageHero title="ĐÀO TẠO" />
      <div className="container training-page">
        <section className="training-direction">
          <img src={mediaSrc(page?.directionImage?.url)} alt={page?.directionImage?.alt || 'Học sinh học tập'} />
          <div>
            <SectionTitle>{page?.directionTitle || 'ĐỊNH HƯỚNG ĐÀO TẠO'}</SectionTitle>
            <p>{page?.directionDescription || 'Trường THCS Nguyễn Văn Linh định hướng đào tạo học sinh phát triển toàn diện về đạo đức, trí tuệ, thể chất và kỹ năng sống; phát huy năng lực cá nhân, tính chủ động, sáng tạo, đáp ứng yêu cầu đổi mới giáo dục.'}</p>
            <div className="direction-values">
              {directionValues.map((item) => {
                const Icon = iconMap[item.icon as keyof typeof iconMap] || BookOpen
                return <article key={item.title}><Icon /><strong>{item.title}</strong><span>{item.description}</span></article>
              })}
            </div>
          </div>
        </section>

        <section>
          <SectionTitle>CHƯƠNG TRÌNH ĐÀO TẠO</SectionTitle>
          {isLoading && <p className="section-note">Đang tải chương trình đào tạo...</p>}
          {isError && <p className="section-note">Chưa tải được dữ liệu chương trình đào tạo.</p>}
          {!isLoading && !isError && programs.length === 0 && <p className="section-note">Chưa có chương trình đào tạo được xuất bản.</p>}
          {programs.length > 0 && (
            <div className="program-grid">
              {programs.map((program) => (
                <article key={program.id}>
                  <Link to={`/dao-tao/${program.slug || program.id}`} aria-label={program.title}>
                    <img src={program.image || '/assets/images/training-program.jpg'} alt={program.title} />
                  </Link>
                  <div>
                    <h3><Link to={`/dao-tao/${program.slug || program.id}`}>{program.title}</Link></h3>
                    <p>{program.summary}</p>
                    <Link to={`/dao-tao/${program.slug || program.id}`}>Xem chi tiết</Link>
                  </div>
                </article>
              ))}
            </div>
          )}
        </section>

        <section>
          <SectionTitle>HOẠT ĐỘNG CHUYÊN MÔN</SectionTitle>
          <div className="professional-grid"><article><Users /><h3>Sinh hoạt chuyên môn</h3><p>Đổi mới phương pháp dạy học, chia sẻ kinh nghiệm.</p></article><article><BookOpen /><h3>Ứng dụng CNTT</h3><p>Tích cực ứng dụng công nghệ thông tin trong dạy và học.</p></article><article><Beaker /><h3>Nghiên cứu khoa học</h3><p>Khuyến khích học sinh tham gia nghiên cứu khoa học kỹ thuật.</p></article><article><ClipboardCheck /><h3>Kiểm tra - Đánh giá</h3><p>Đánh giá đa dạng, công khai, công bằng, khách quan.</p></article><article><BarChart3 /><h3>Bồi dưỡng giáo viên</h3><p>Nâng cao năng lực chuyên môn, đáp ứng yêu cầu đổi mới.</p></article></div>
        </section>

        <section>
          <SectionTitle>{page?.achievementTitle || 'THÀNH TÍCH ĐÀO TẠO'}</SectionTitle>
          <div className="training-achievement">
            <div>
              <Award className="big-trophy" />
              <ul>{achievementBullets.map((item) => <li key={item.text}>{item.text}</li>)}</ul>
            </div>
            <div>
              {achievementStats.map((item) => {
                const Icon = iconMap[item.icon as keyof typeof iconMap] || Award
                return <article key={`${item.value}-${item.label}`}><Icon /><strong>{item.value}</strong><span>{item.label}</span></article>
              })}
            </div>
          </div>
        </section>

        <section className="teaching-gallery">
          <SectionTitle>{page?.galleryTitle || 'HÌNH ẢNH HOẠT ĐỘNG DẠY VÀ HỌC'}</SectionTitle>
          <div>{galleryImages.map((image) => <img key={image.url} src={mediaSrc(image.url, '/assets/images/training-main.jpg')} alt={image.alt || 'Hoạt động dạy học'} />)}</div>
        </section>
      </div>
    </>
  )
}

export function TrainingDetailPage() {
  const { slug } = useParams()
  const { data: program, isLoading, isError } = useQuery({
    queryKey: ['program', slug],
    queryFn: () => api.getProgram(slug || ''),
    enabled: Boolean(slug),
  })

  if (isLoading) return (
    <>
      <PageHero title="ĐÀO TẠO" />
      <div className="container generic-page"><p>Đang tải chi tiết chương trình đào tạo...</p></div>
    </>
  )

  if (isError || !program) return (
    <>
      <PageHero title="ĐÀO TẠO" />
      <div className="container generic-page"><p>Không tìm thấy chương trình đào tạo.</p></div>
    </>
  )

  const typeLabel = programTypeLabels[program.programType || ''] || 'Chương trình đào tạo'

  return (
    <>
      <div className="container breadcrumb-line">
        <Link to="/">Trang chủ</Link><span>›</span>
        <Link to="/dao-tao">Đào tạo</Link><span>›</span>
        <span>Chi tiết</span>
      </div>
      <article className="container training-detail-page">
        <span className="badge">{typeLabel}</span>
        <h1>{program.title}</h1>
        {program.summary && <p className="lead">{program.summary}</p>}
        <img className="training-detail-cover" src={program.image || '/assets/images/training-program.jpg'} alt={program.title} />
        <div className="training-detail-meta">
          {program.gradeLevels && program.gradeLevels.length > 0 && <span>Khối lớp: {program.gradeLevels.join(', ')}</span>}
          {program.subjects && program.subjects.length > 0 && <span>Môn học: {program.subjects.join(', ')}</span>}
        </div>
        <div className="article-body">
          {program.content && typeof program.content === 'object'
            ? <TrainingRichText content={program.content} />
            : <p>{program.summary || 'Nội dung chi tiết chương trình đào tạo đang được cập nhật.'}</p>
          }
        </div>
        <Link className="back-link" to="/dao-tao">← Quay lại trang đào tạo</Link>
      </article>
    </>
  )
}

function TrainingRichText({ content }: { content: unknown }) {
  const root = content && typeof content === 'object' && 'root' in content ? (content as any).root : undefined
  const children = Array.isArray(root?.children) ? root.children : []
  if (!children.length) return null
  return <div className="rich-text">{children.map((node: any, index: number) => renderTrainingNode(node, index))}</div>
}

function renderTrainingNode(node: any, key: number) {
  if (!node) return null
  switch (node.type) {
    case 'paragraph':
      return <p key={key}>{node.children?.map((child: any, index: number) => renderTrainingLeaf(child, index))}</p>
    case 'heading': {
      const Tag = `h${node.tag?.replace('h', '') || '2'}` as 'h2' | 'h3' | 'h4'
      return <Tag key={key}>{node.children?.map((child: any, index: number) => renderTrainingLeaf(child, index))}</Tag>
    }
    case 'list':
      return node.listType === 'number'
        ? <ol key={key}>{node.children?.map((child: any, index: number) => renderTrainingNode(child, index))}</ol>
        : <ul key={key}>{node.children?.map((child: any, index: number) => renderTrainingNode(child, index))}</ul>
    case 'listitem':
      return <li key={key}>{node.children?.map((child: any, index: number) => renderTrainingLeaf(child, index))}</li>
    case 'quote':
      return <blockquote key={key}>{node.children?.map((child: any, index: number) => renderTrainingNode(child, index))}</blockquote>
    default:
      return null
  }
}

function renderTrainingLeaf(leaf: any, key: number) {
  if (!leaf) return null
  if (leaf.type !== 'text') return null
  let element = leaf.text
  if (leaf.format & 1) element = <strong>{element}</strong>
  if (leaf.format & 2) element = <em>{element}</em>
  if (leaf.format & 8) element = <u>{element}</u>
  return <span key={key}>{element}</span>
}
